import { FC, useEffect, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { createRouter, Link, RouterProvider } from '@tanstack/react-router'

import { Button } from '@forto-dev/phoenix-ui-core'
import { Alert, Spinner as SpinnerComponent } from '@forto-dev/phoenix-ui-core/common'
import { Splash } from '@forto-dev/phoenix-ui-core/ship'

import { getConfig } from '@/app/config'
import { useAuth } from '@/features/auth'
import { useUserProfile } from '@/features/auth/api/useUserProfile'
import { routeTree } from '@/routes'
import { useFeatureFlag } from '@/utils/featureFlag/useFeatureFlag'
import { useLocalisation } from '@/utils/localisation'
import { userlane } from '@/utils/userlane/UserlaneService'

import { createErrorFallback } from './create-error-fallback'
import { Spinner } from './spinner'

const DefaultNotFoundComponent = () => {
  const { translate } = useLocalisation()

  return (
    <Splash>
      <Alert variant="negative" title={translate('navigation.not_found.title')} />
      <Button asChild>
        <Link to="/">{translate('navigation.not_found.back')}</Link>
      </Button>
    </Splash>
  )
}

const router = createRouter({
  routeTree,
  basepath: getConfig().APP_BASE_URL ? `${getConfig().APP_BASE_URL}/` : '/',
  // TODO: Better error message
  defaultErrorComponent: createErrorFallback('An error occurred while loading this section'),
  defaultPendingComponent: () => (
    <Splash>
      <SpinnerComponent size="lg" />
    </Splash>
  ),
  defaultNotFoundComponent: DefaultNotFoundComponent,
  // Based on https://tanstack.com/router/latest/docs/framework/react/guide/data-loading#passing-all-loader-events-to-an-external-cache
  defaultPreloadStaleTime: 0,
  // This means that the data will be fetched using the route's `loader` when a user hovers or `touchstart`s a link
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export const Router: FC = () => {
  const { isReady, initialData, isAuthenticated, user, loggedInAsCustomer } = useAuth()
  const queryClient = useQueryClient()
  const { isUserProfileLoading } = useUserProfile()
  const { isFeatureFlagsLoading, isFeatureEnabled } = useFeatureFlag()
  const routerContext = useMemo(() => {
    return {
      isReady,
      isFeatureEnabled,
      initialData,
    }
  }, [isReady, initialData, isFeatureEnabled])

  useEffect(() => {
    if (isAuthenticated && user && initialData.data) {
      userlane.init(user, initialData.data, loggedInAsCustomer)
    }
  }, [isAuthenticated, user, initialData.data, loggedInAsCustomer])

  useEffect(() => {
    router.invalidate()
  }, [routerContext])

  if (isUserProfileLoading || isFeatureFlagsLoading) {
    return <Spinner />
  }

  return isReady ? (
    <RouterProvider
      context={{
        isReady,
        isFeatureEnabled,
        initialData: initialData.data ?? undefined,
        queryClient,
      }}
      router={router}
    />
  ) : (
    <Spinner />
  )
}
